/**
 * pageDataCache — узкоспециализированный localStorage-кэш только для /about.
 *
 * ── Зачем отдельный слой? ─────────────────────────────────────────────────
 * Основной cache архитектуры живёт в `src/lib/globalDataCache/*` — он держит
 * структурированные данные (products, portfolio, news, homeData, esgData,
 * heroTexts/Media, footerData) с in-flight дедупликацией, listeners и
 * критическим/idle prefetch'ем.
 *
 * About — единственная страница, которая использует *этот* helper вместо
 * globalDataCache. Логика About была настроена через несколько раундов
 * language-switch фиксов (synchronous reset на смене языка, FactoryStorytelling
 * fade-in кадр), и миграция на общий cache требует осторожной отдельной
 * итерации — поэтому помощник оставлен как тонкая прослойка над localStorage.
 *
 * ── Поведение ─────────────────────────────────────────────────────────────
 * 1. При загрузке страницы — мгновенно читается кэш из localStorage.
 * 2. Данные рендерятся сразу (без мигания или задержки).
 * 3. В фоне загружаются свежие данные из БД.
 * 4. Кэш обновляется. Следующий визит — снова мгновенный рендер.
 *
 * ── Что НЕ хранится здесь ─────────────────────────────────────────────────
 * - ESG страница использует globalDataCache/esg.ts (own in-memory + localStorage)
 * - BecomeDealer использует свой sessionStorage cache внутри страницы
 * - Hero-блоки и list-данные идут через globalDataCache
 */

const CACHE_PREFIX = 'sargo_page_cache_';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 минут — после этого считаем устаревшим, но всё равно показываем

interface CacheEntry<T> {
  data: T;
  savedAt: number;
  lang: string;
}

function cacheKey(page: string, lang: string): string {
  return `${CACHE_PREFIX}${page}_${lang}`;
}

/** Прочитать кэш. Возвращает данные или null если кэша нет. */
export function readCache<T>(page: string, lang: string): T | null {
  try {
    const raw = localStorage.getItem(cacheKey(page, lang));
    if (!raw) return null;
    const entry: CacheEntry<T> = JSON.parse(raw);
    if (!entry?.data) return null;
    return entry.data;
  } catch {
    return null;
  }
}

/** Записать данные в кэш. */
export function writeCache<T>(page: string, lang: string, data: T): void {
  try {
    const entry: CacheEntry<T> = { data, savedAt: Date.now(), lang };
    localStorage.setItem(cacheKey(page, lang), JSON.stringify(entry));
  } catch {
    // localStorage недоступен — игнорируем
  }
}

/** Очистить кэш страницы (вызывается из админки после сохранения). */
export function invalidatePageCache(page: string): void {
  try {
    const keys = Object.keys(localStorage).filter(k =>
      k.startsWith(`${CACHE_PREFIX}${page}_`)
    );
    keys.forEach(k => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

// ─── /about специфичные типы ──────────────────────────────────────────────────

export interface AboutDbBlock {
  id: string;
  blockKey: string;
  lang: string;
  subtitle: string;
  title: string;
  description: string;
  image: string;
  cam: string;
  bgNumber: string;
}

export interface AboutCacheData {
  blocks: AboutDbBlock[];
  futureTitle: string;
  futureText: string;
}

export function readAboutCache(lang: string): AboutCacheData | null {
  return readCache<AboutCacheData>('about', lang);
}

export function writeAboutCache(lang: string, data: AboutCacheData): void {
  writeCache('about', lang, data);
}

// ─── ESG ──────────────────────────────────────────────────────────────────
// ESG больше не использует этот слой — он перешёл на globalDataCache/esg.ts
// (in-memory cache + sargo_esg_${lang} localStorage backing).
//
// ─── BecomeDealer ─────────────────────────────────────────────────────────
// BecomeDealer держит свой собственный 3-уровневый cache внутри страницы
// (memory _memCache + sessionStorage `sargo_bd_cms_${lang}` + primeCache при
// импорте модуля). Унификация требует переноса primeCache-on-import паттерна
// в globalDataCache, что задевает language-switch — отложено на отдельный
// проход.
